import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { SCHEDULE_DATES_MAX, ScheduleAfterServiceAnchor, ScheduleAfterServiceRule, ScheduleCalendarRule, ScheduleContext, ScheduleDailyPreset, ScheduleLeeway, ScheduleRule, ScheduleEngine, ScheduleKind, ScheduleMonthlyOn, ScheduleNonWorkingDayPolicy, ScheduleNth, ScheduleRepeat, ScheduleWeekday } from '@jms/model/scheduling';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ButtonChipComponent, ButtonComponent, ButtonDisplayStyle, ButtonDropdownComponent, ButtonRadioComponent, ButtonSelectComponent, ButtonSize, ButtonTabComponent, ButtonToggleComponent } from '@workbuddy/ui/v2/components/button';
import { MiniCalendarComponent } from '@workbuddy/ui/v2/components/calendar';
import { DayOfMonthGridComponent, DayOfMonthValue } from '@workbuddy/ui/v2/components/day.grid';
import { NumberStepperComponent } from '@workbuddy/ui/v2/components/stepper';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { Command } from '@workbuddy/ui/model';
import { ScheduleEditorDraft, addScheduleDate, createDraft, draftIssues, kindUsesFirstDue, removeScheduleDate, repeatUsesMonthDays, repeatUsesWeekdays, toModel, toResult } from './schedule.editor.draft';
import { AFTER_SERVICE_ANCHOR_OPTIONS, AFTER_SERVICE_UNIT_OPTIONS, ENDS_OPTIONS, MONTH_CHIP_ROWS, NON_WORKING_DAY_OPTIONS, ORDINAL_OPTIONS, SCHEDULE_KIND_OPTIONS, SCHEDULE_REPEAT_OPTIONS, SPECIFIC_DATES_REPEAT_ID, WEEKDAY_OPTIONS, WEEKDAY_TOGGLES, addCalendarDays, fixedMonthLabels, formatScheduleDate, formatSchedulePreview, isoParityWeeks, isoWeekNumber, quarterlyAnchorMonths, startOfIsoWeek } from './schedule.editor.options';
import { ScheduleMode } from './schedule.editor.context';
import { ScheduleEditorResult } from './schedule.editor.launcher.contract';
import { scheduleEditorBridgeStyles, scheduleEditorStyleId } from './schedule.editor.theme';

/** How many dates the next-due strip shows. Four fits the 480 dialog on one line at every repeat. */
const PREVIEW_COUNT = 4;

/** How far ahead the preview looks for those four dates. A yearly rule with a month filter needs years. */
const PREVIEW_HORIZON_YEARS = 5;

/**
 * Daily presets in the order the radio rows read, with the copy the trade uses for each.
 *
 * Working days leads, and it is the only preset that does not name a fixed day set: it resolves against
 * whatever the line — or its plan, or the tenant — calls a working week. It is also the cadence an
 * unscheduled task already has, so putting it first lets a user make that default explicit without
 * hunting for it, and lets them see at a glance that the fixed Mon–Fri row below is a different thing.
 */
const DAILY_PRESETS: readonly { preset: ScheduleDailyPreset; label: string }[] = [
  { preset: 'workingDays', label: 'Working days (from the plan)' },
  { preset: 'everyDay', label: 'Every day' },
  { preset: 'everyNDays', label: 'Every' },
  { preset: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { preset: 'sixDay', label: '6-day (Mon–Sat)' },
  { preset: 'weekend', label: 'Weekend (Sat–Sun)' },
];

/**
 * The tack-on warning under the Per visit tab.
 *
 * Per visit is the one kind that creates nothing. A scheduler who picks it and then goes looking for the
 * task in upcoming work will not find it, so the tab says so before they save rather than after.
 */
const PER_VISIT_FOOTNOTE = 'Tack-on mode — this task never creates dates of its own; it joins visits other tasks cause. It will not appear in upcoming work.';

/** The First due default. Named rather than dated on its own so the row reads as a choice, not a value. */
const WITH_SERVICE_LINE_LABEL = 'With the Service Line';

/** Trails the resolved date while First due is on its default. */
const WITH_SERVICE_LINE_HINT = 'with the Service Line';

/** Empty last-serviced is a real answer, so the field says what is missing rather than 'Select a date'. */
const LAST_SERVICED_PLACEHOLDER = 'Not recorded';

/**
 * The template footer, replacing 'Effective from … · timezone from the line'.
 *
 * The live sentence names two things a definition does not have — a start date and a line whose
 * timezone it inherits — so restating it under a template editor would describe a relationship that
 * only comes into existence when a plan is created from the template.
 */
const TEMPLATE_FOOTER_NOTE = 'Applies to plans created from this template';

/**
 * The template Next due caption.
 *
 * The dates below it are real engine output, but they are anchored on today because a definition has
 * no contract start — so they are an illustration of the cadence, not the visits anyone will work.
 * Labelling the section 'Next due' unqualified would promise a schedule that does not exist yet.
 */
const TEMPLATE_PREVIEW_LABEL = 'Example dates';

const TEMPLATE_PREVIEW_NOTE = 'Based on today — a plan created from this template starts from its own dates';

/**
 * The four fortnight phase cards. A/B keep a strict rhythm; the ISO pair follow the printed calendar.
 *
 * Every card carries a sublabel naming ISO week numbers, so all four occupy the same two-line box.
 * Without it the grid renders two tall cards beside two short ones.
 */
const WEEK_SELECTORS: readonly { value: 'A' | 'B' | 'isoOdd' | 'isoEven'; label: string }[] = [
  { value: 'A', label: 'Week A' },
  { value: 'B', label: 'Week B' },
  { value: 'isoOdd', label: 'Odd weeks (ISO)' },
  { value: 'isoEven', label: 'Even weeks (ISO)' },
];

/**
 * The schedule pattern editor: one 480-wide modal that covers every way a Service Line Task can recur.
 *
 * Three kinds share one skeleton — header, kind tabs, the kind's own block, a next-due preview, and an
 * effective-from footer — so a user who learns the Calendar tab already knows the other two. Within
 * Calendar every repeat follows the same Repeat → On → Ends → non-working-day order for the same
 * reason.
 *
 * Two rules hold the whole component together:
 *
 *  - No date arithmetic here. Every date shown, including the "last one" under an after-a-count end,
 *    comes from `ScheduleEngine`, which the API also runs. If the preview and the materialised
 *    dates could be computed two different ways they would eventually disagree, and the user would
 *    have trusted the wrong one.
 *  - `today` is injected, never read from a clock. The engine is deterministic on `ctx.today`, and a
 *    preview that quietly re-rendered at midnight is a test that cannot be written.
 *
 * Exceptions (skips, extra dates, blackouts) are deliberately absent: they belong to the schedule card,
 * not to the pattern.
 */
@Component({
  selector: 'wb-schedule-editor-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.readonly]': '$readonly()',
  },
  imports: [OverlayModule, ButtonChipComponent, ButtonComponent, ButtonDropdownComponent, ButtonRadioComponent, ButtonSelectComponent, ButtonTabComponent, ButtonToggleComponent, DayOfMonthGridComponent, MiniCalendarComponent, NumberStepperComponent],
  templateUrl: './schedule.editor.dialog.component.html',
  styleUrls: ['./schedule.editor.dialog.component.less'],
})
export class ScheduleEditorDialogComponent {

  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = injectDialogRef<ScheduleEditorResult>();
  private readonly engine = new ScheduleEngine();

  /** Shown small-caps above the 'Schedule' title, so the user knows which task they are editing. */
  public readonly $taskName = input('', { alias: 'taskName' });

  public readonly $model = input<ScheduleRule | undefined>(undefined, { alias: 'model' });

  /** Locked state: every control disabled and no Save. The card decides when this applies. */
  public readonly $readonly = input(false, { alias: 'readonly' });

  /** Line anchor. Doubles as the after-service first-due default and as the calendar DTSTART. */
  public readonly $effectiveFrom = input.required<string>({ alias: 'effectiveFrom' });
  public readonly $effectiveTo = input<string | undefined>(undefined, { alias: 'effectiveTo' });

  public readonly $timezone = input('UTC', { alias: 'timezone' });

  /** Injected current local date. The engine's only clock — see the class comment. */
  public readonly $today = input.required<string>({ alias: 'today' });

  /** Tenant working week, 1 = Monday. Only the shift policies read it. */
  public readonly $workWeek = input<ScheduleWeekday[]>([1, 2, 3, 4, 5], { alias: 'workWeek' });

  public readonly $holidays = input<string[]>([], { alias: 'holidays' });

  /**
   * The kinds this caller allows, or undefined for all of them.
   *
   * A TEMPLATE task can hold only a cadence that still means the same thing when the definition is
   * applied to a contract starting in any year, so 'dates' is not offered there — nor is the
   * 'Specific dates' repeat, which is the same kind reached through the Repeat row. A live task
   * passes nothing and every option renders, which is why the default is "all" rather than a list
   * repeated at every live call site.
   *
   * The server rejects a forbidden value regardless (WBS-10989). This exists so the user is never
   * shown a control that leads to a rejection: gating the UI and validating the payload answer two
   * different questions, and only the second one can be trusted.
   */
  public readonly $allowedKinds = input<readonly ScheduleKind[] | undefined>(undefined, { alias: 'allowedKinds' });

  /** The calendar endings this caller allows, or undefined for all. Templates omit 'onDate'. */
  public readonly $allowedEndings = input<readonly string[] | undefined>(undefined, { alias: 'allowedEndings' });

  /**
   * Whether this is a live plan's task or a reusable definition. Defaults to 'live' so every existing
   * caller keeps the editor it already had.
   *
   * A template has no contract behind it: no start date to anchor to, no line blackouts or holidays to
   * work around, and no service history to chain from. Everything below that reads this input is
   * removing a control that would otherwise ask the user for a date the definition cannot hold, or a
   * caption that would state a relationship to a plan that does not exist yet.
   */
  public readonly $mode = input<ScheduleMode>('live', { alias: 'mode' });

  /** Read once here so no template binding or computed has to spell the comparison out again. */
  protected readonly $isTemplate = computed(() => this.$mode() === 'template');

  /** 'Next due' on a plan; an example on a template, where the dates illustrate rather than commit. */
  protected readonly $previewLabel = computed(() => (this.$isTemplate() ? TEMPLATE_PREVIEW_LABEL : 'Next due'));

  /** The template's own footer, or the live 'Effective from … · timezone from the line'. */
  protected readonly $footerNote = computed(() => (this.$isTemplate() ? TEMPLATE_FOOTER_NOTE : `Effective from ${formatScheduleDate(this.$anchor())} · timezone from the line`));

  protected readonly templatePreviewNote = TEMPLATE_PREVIEW_NOTE;

  /**
   * The task's stored start override, YYYY-MM-DD, or undefined for "with the Service Line". Edited on
   * the First due row and handed back on save — it is the task's own column, not part of the schedule.
   */
  public readonly $startsOn = input<string | undefined>(undefined, { alias: 'startsOn' });

  /** The task's stored `lastCompleted`. Seeds the after-service chain; empty means nothing recorded. */
  public readonly $lastCompleted = input<string | undefined>(undefined, { alias: 'lastCompleted' });

  /**
   * The working copy. A `linkedSignal` rather than a `signal` seeded in the constructor: signal inputs
   * are not bound yet at construction, so an eager seed would always produce the empty default and
   * strand the task's existing schedule. Every edit writes here; nothing writes to the input.
   *
   * The source is the three seed inputs together, not just the model: `startsOn` and `lastCompleted`
   * are part of the same one answer, so re-seeding on any of them keeps the draft whole.
   */
  protected readonly $draft = linkedSignal<{ model: ScheduleRule | undefined; startsOn?: string; lastServiced?: string }, ScheduleEditorDraft>({
    source: () => ({ model: this.$model(), startsOn: this.$startsOn(), lastServiced: this.$lastCompleted() }),
    computation: seed => createDraft(seed.model, { startsOn: seed.startsOn, lastServiced: seed.lastServiced }),
  });

  /**
   * The three option rows a caller can narrow, filtered rather than disabled.
   *
   * A disabled tab still tells the user the capability exists and they may not have it, which is the
   * wrong message: a template task cannot name calendar dates at all, so there is nothing being
   * withheld. Filtering is also what keeps a forbidden value unreachable — a disabled control can
   * still be the active one if the stored model already holds it.
   */
  protected readonly $kindOptions = computed(() => filterOptions(SCHEDULE_KIND_OPTIONS, this.$allowedKinds()));

  /**
   * 'Specific dates' is the 'dates' KIND wearing a Repeat row entry, so it leaves with that kind:
   * a caller that disallows 'dates' must not be shown the other door to it.
   *
   * Template mode drops it on its own authority as well as through the allow-list. Calendar days are
   * the one thing a definition provably cannot hold, so the row must not offer them even to a
   * template host that passed no `allowedKinds` — the two conditions answer to different callers and
   * either one alone is reason enough.
   */
  protected readonly $repeatOptions = computed(() => {
    const kinds = this.$allowedKinds();
    if (!this.$isTemplate() && (!kinds || kinds.includes('dates'))) return SCHEDULE_REPEAT_OPTIONS;

    return SCHEDULE_REPEAT_OPTIONS.filter(option => option._id !== SPECIFIC_DATES_REPEAT_ID);
  });

  protected readonly $endsOptions = computed(() => filterOptions(ENDS_OPTIONS, this.$allowedEndings()));

  protected readonly unitOptions = AFTER_SERVICE_UNIT_OPTIONS;
  protected readonly anchorOptions = AFTER_SERVICE_ANCHOR_OPTIONS;
  protected readonly ordinalOptions = ORDINAL_OPTIONS;
  protected readonly weekdayOptions = WEEKDAY_OPTIONS;
  protected readonly weekdayToggles = WEEKDAY_TOGGLES;
  protected readonly monthChipRows = MONTH_CHIP_ROWS;
  protected readonly nonWorkingDayOptions = NON_WORKING_DAY_OPTIONS;
  protected readonly dailyPresets = DAILY_PRESETS;
  protected readonly perVisitFootnote = PER_VISIT_FOOTNOTE;
  protected readonly withServiceLineLabel = WITH_SERVICE_LINE_LABEL;
  protected readonly weekSelectors = WEEK_SELECTORS;
  protected readonly primary = ButtonDisplayStyle.Primary;
  /** Boards run Small; Medium read a size too large next to them. */
  protected readonly controlSize = ButtonSize.Small;

  /** Below the field, flipping above when the panel would fall off the bottom of the dialog. */
  protected readonly firstDuePositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  protected readonly titleId = `wb-schedule-editor-title-${crypto.randomUUID()}`;

  protected readonly $kind = computed(() => this.$draft().kind);
  protected readonly $calendar = computed(() => this.$draft().calendar);
  protected readonly $repeat = computed(() => this.$calendar().repeat);

  /**
   * Everything the shared validator objects to, in field order. Save is gated on this being empty and
   * the first entry is shown inline, so the reason Save is unavailable is always on screen rather than
   * left for the user to deduce from a greyed button.
   */
  protected readonly $issues = computed(() => draftIssues(this.$draft()));
  protected readonly $valid = computed(() => this.$issues().length === 0);
  protected readonly $firstIssue = computed(() => this.$issues()[0]?.message ?? '');
  protected readonly $canSave = computed(() => !this.$readonly() && this.$valid());

  /**
   * What the Repeat row shows as active. A dates schedule left the calendar branch entirely, so the
   * row reports the sentinel rather than whichever repeat the abandoned calendar branch still holds.
   */
  protected readonly $repeatSelection = computed(() => (this.$kind() === 'dates' ? SPECIFIC_DATES_REPEAT_ID : this.$repeat()));

  /** The Repeat row and everything under it belong to the two calendar-family kinds. */
  protected readonly $showCalendarPane = computed(() => this.$kind() === 'calendar');
  protected readonly $showDatesPane = computed(() => this.$kind() === 'dates');

  protected readonly $showWeekdayToggles = computed(() => repeatUsesWeekdays(this.$repeat()));
  protected readonly $showMonthDayBlock = computed(() => repeatUsesMonthDays(this.$repeat()));

  /**
   * Whether the seasonal months section is open.
   *
   * A `linkedSignal` off the seeded draft rather than a plain derivation of `months.length`: it has to
   * start open for a schedule that already carries a filter, but a user who opens it and then unticks
   * every chip must not have it collapse under them mid-edit.
   */
  protected readonly $seasonalOpen = linkedSignal<ScheduleRule | undefined, boolean>({
    source: () => this.$model(),
    computation: model => (model?.calendar?.months?.length ?? 0) > 0,
  });

  protected readonly $showSeasonalToggle = computed(() => this.$repeat() !== 'yearly');

  protected readonly $effectiveFromLabel = computed(() => formatScheduleDate(this.$effectiveFrom()));

  /**
   * Where the rule starts: a calendar schedule's First due when picked, else the line's start. The
   * server starts the rule at the later of the two, so Week A, quarterly months, the after-count end
   * and the preview all read this. Other kinds never save a First due, so they start at the line.
   * The picker only allows dates on or after the line start, so the override is always the later one.
   */
  protected readonly $anchor = computed(() => (kindUsesFirstDue(this.$kind()) ? this.$draft().startsOn : undefined) ?? this.$effectiveFrom());

  /**
   * Fortnight phase A starts on the anchor week, B the week after — both named by their MONDAY.
   *
   * Weeks start Monday everywhere here (WKST=MO), so an effective-from of Friday 28 August belongs to
   * the week starting Monday 24 August. Naming the 28th would contradict the rule that gets compiled.
   */
  protected readonly $weekAStart = computed(() => formatScheduleDate(startOfIsoWeek(this.$anchor())));
  protected readonly $weekBStart = computed(() => formatScheduleDate(addCalendarDays(startOfIsoWeek(this.$anchor()), 7)));

  /**
   * Whether any month chips are picked.
   *
   * compile() drops INTERVAL entirely once BYMONTH is present, so a months filter is not a
   * refinement of the interval — it replaces it. Everything below keys off this one fact.
   */
  protected readonly $hasFixedMonths = computed(() => (this.$calendar().months ?? []).length > 0);

  /**
   * The 'Every N months' stepper, shown only when the interval is what actually governs the rule.
   *
   * With months picked the stepper is inert — moving it changes nothing that compiles — and its
   * 'lands in' hint contradicts the chips the user just ticked. Clearing the chips brings it back.
   */
  protected readonly $showMonthIntervalRow = computed(() => this.$repeat() === 'quarterly' && !this.$hasFixedMonths());

  /** Replaces the interval hint with the months actually compiled, in calendar order. */
  protected readonly $fixedMonthsNote = computed(() => {

    if (this.$repeat() !== 'quarterly' && this.$repeat() !== 'monthly') return '';
    if (!this.$hasFixedMonths()) return '';

    return `fixed months: ${fixedMonthLabels(this.$calendar().months ?? [])}`;
  });

  protected readonly $quarterlyAnchors = computed(() => {

    const anchorMonth = Number(this.$anchor().slice(5, 7));

    return quarterlyAnchorMonths(anchorMonth, this.$calendar().monthInterval ?? 3);
  });

  /** Next four effective dates, engine-computed. Empty for per visit and for an exhausted schedule. */
  protected readonly $preview = computed(() => {

    if (!this.$valid()) return [];

    // A dates list is finite and hand-picked, so its preview is the list itself — anchored at the line
    // rather than at today, or a list the user just typed out would read 'No upcoming dates' the moment
    // its first entries are behind them.
    const from = this.$kind() === 'dates' ? this.$anchor() : this.$today();

    return this.engine
      .occurrencesBetween(toModel(this.$draft()), from, this.addYears(from, PREVIEW_HORIZON_YEARS), this.context())
      .slice(0, PREVIEW_COUNT);
  });

  protected readonly $previewText = computed(() => formatSchedulePreview(this.$preview()));

  /**
   * The date of the final occurrence under an after-a-count end.
   *
   * The count is a pattern count, so "12 services" is not "12 months" — only the engine knows where the
   * twelfth actually lands once the non-working-day policy has had its say.
   */
  protected readonly $lastOccurrence = computed(() => {

    const ends = this.$calendar().ends;
    if (this.$kind() !== 'calendar' || ends.type !== 'afterCount' || !this.$valid()) return '';

    const from = this.$anchor();
    const occurrences = this.engine.occurrencesBetween(toModel(this.$draft()), from, this.addYears(from, PREVIEW_HORIZON_YEARS * 2), this.context());
    const last = occurrences[occurrences.length - 1];

    return last ? formatScheduleDate(last) : '';
  });

  /** 'services · last one Tue 1 Sep', or just 'services' while the last date is not yet computable. */
  protected readonly $endsCountNote = computed(() => {

    const last = this.$lastOccurrence();

    return last ? `services · last one ${last}` : 'services';
  });

  public constructor() {

    DesignSystem.Mount(this.document, scheduleEditorStyleId, scheduleEditorBridgeStyles);
  }

  // ── Kind ────────────────────────────────────────────────────────────

  protected onKindChange(kind: ScheduleKind): void {

    if (this.$readonly() || !kind) return;

    this.$draft.update(draft => ({ ...draft, kind }));
  }

  // ── Per visit ───────────────────────────────────────────────────────

  protected readonly $everyVisit = computed(() => this.$draft().perVisit.nth <= 1);

  protected onEveryVisit(everyVisit: boolean): void {

    if (this.$readonly()) return;

    this.$draft.update(draft => ({ ...draft, perVisit: { nth: everyVisit ? 1 : Math.max(2, draft.perVisit.nth) } }));
  }

  protected onPerVisitNth(nth: number): void {

    if (this.$readonly()) return;

    this.$draft.update(draft => ({ ...draft, perVisit: { nth } }));
  }

  // ── After service ───────────────────────────────────────────────────

  protected onAfterServiceInterval(interval: number): void {

    this.patchAfterService({ interval });
  }

  protected onAfterServiceUnit(unit: 'days' | 'months' | 'years'): void {

    if (!unit) return;

    this.patchAfterService({ unit });
  }

  protected onLeewayValue(side: 'earlyLeeway' | 'lateLeeway', value: number): void {

    this.patchLeeway(side, { ...this.$draft().afterService[side], value: Math.max(0, value ?? 0) });
  }

  protected onLeewayUnit(side: 'earlyLeeway' | 'lateLeeway', unit: ScheduleAfterServiceRule['unit']): void {

    if (!unit) return;

    this.patchLeeway(side, { ...this.$draft().afterService[side], unit });
  }

  protected onAfterServiceAnchor(anchor: ScheduleAfterServiceAnchor): void {

    if (!anchor) return;

    this.patchAfterService({ anchor });
  }

  /**
   * When the task was last serviced — the chain's seed, and a TASK field rather than a schedule one.
   *
   * It replaces the old first-due input: an after-service task is due `interval` after the last
   * service, so the honest question is when that service happened, not when the next one falls. Empty
   * is a real answer (never serviced) and means the chain starts at the line's effective-from.
   */
  protected readonly $lastServicedLabel = computed(() => {

    const lastServiced = this.$draft().lastServiced;

    return lastServiced ? formatScheduleDate(lastServiced) : LAST_SERVICED_PLACEHOLDER;
  });

  protected readonly $hasLastServiced = computed(() => !!this.$draft().lastServiced);

  protected readonly $lastServicedOpen = signal(false);

  /**
   * A service that has not happened yet is not a record of one, so tomorrow is not selectable.
   * Bound as a field so the arrow keeps its `this` when the calendar calls it per rendered day.
   */
  protected readonly isFutureDate = (date: string): boolean => date > this.$today();

  /** Greys out every day the line does not cover, so an unusable date cannot be picked in the first place. */
  protected readonly isOutsideLine = (date: string): boolean => !this.isWithinLine(date);

  protected onLastServiced(lastServiced: string): void {

    this.$lastServicedOpen.set(false);
    if (this.$readonly() || lastServiced > this.$today()) return;

    this.$draft.update(draft => ({ ...draft, lastServiced }));
  }

  protected clearLastServiced(): void {

    if (this.$readonly()) return;

    this.$lastServicedOpen.set(false);
    this.$draft.update(draft => ({ ...draft, lastServiced: undefined }));
  }

  protected toggleLastServiced(): void {

    if (this.$readonly()) return;

    this.$lastServicedOpen.update(open => !open);
  }

  protected closeLastServiced(): void {

    this.$lastServicedOpen.set(false);
  }

  // ── First due (task startsOn) ───────────────────────────────────────

  /**
   * Whether the First due section shows at all.
   *
   * Calendar repeats only. A per-visit task creates no dates to start, a dates list already names its
   * own first day, and after-service chains off the last service — on all three a start override would
   * be a control that changes nothing.
   *
   * Never in template mode. `startsOn` is an absolute date that only a contract can supply, and it is
   * one of the standing-only fields a `PlannedTask` on a TEMPLATE parent may not hold — the server
   * rejects it outright — so the row would be asking for an answer with nowhere to go.
   */
  protected readonly $showFirstDue = computed(() => !this.$isTemplate() && kindUsesFirstDue(this.$kind()));

  /**
   * Whether the Last serviced row shows. Live only, for the same reason First due is.
   *
   * `lastCompleted` records that a service actually happened, which is history a definition has none
   * of — and the template document has no field to keep it in. Without the row the after-service
   * cadence still authors fine: 'every N units after each completed service' is the whole rule, and
   * the chain's seed is supplied by the plan the template is applied to.
   */
  protected readonly $showLastServiced = computed(() => !this.$isTemplate());

  protected readonly $hasStartOverride = computed(() => !!this.$draft().startsOn);

  /**
   * The First due field reads as a date: the date it resolves to leads, and 'with the Service Line'
   * trails as a hint while nothing is picked. Hiding the date behind the default's name would make the
   * user open the picker just to learn when the first service falls.
   */
  protected readonly $firstDueDate = computed(() => formatScheduleDate(this.$anchor()));

  protected readonly $firstDueHint = computed(() => (this.$hasStartOverride() ? '' : WITH_SERVICE_LINE_HINT));

  protected readonly $firstDueOpen = signal(false);

  /** The override may not precede the line: a task cannot start before the line that carries it. */
  protected readonly $firstDueMin = computed(() => this.$effectiveFrom());
  protected readonly $firstDueMax = computed(() => this.$effectiveTo());

  protected onFirstDue(startsOn: string): void {

    this.$firstDueOpen.set(false);
    if (this.$readonly() || !this.isWithinLine(startsOn)) return;

    this.$draft.update(draft => ({ ...draft, startsOn }));
  }

  /** Back to the line's own start. Undefined, not the effective-from date — the task stores no override. */
  protected clearFirstDue(): void {

    if (this.$readonly()) return;

    this.$firstDueOpen.set(false);
    this.$draft.update(draft => ({ ...draft, startsOn: undefined }));
  }

  protected toggleFirstDue(): void {

    if (this.$readonly()) return;

    this.$firstDueOpen.update(open => !open);
  }

  protected closeFirstDue(): void {

    this.$firstDueOpen.set(false);
  }

  // ── Specific dates ──────────────────────────────────────────────────

  protected readonly datesMax = SCHEDULE_DATES_MAX;

  protected readonly datesHint = `within the line's dates · max ${SCHEDULE_DATES_MAX}`;

  /** The list as rows, each carrying the raw value the remove button needs and the label it shows. */
  protected readonly $dateRows = computed(() => this.$draft().dates.dates.map(date => ({ date, label: formatScheduleDate(date) })));

  protected readonly $datesFull = computed(() => this.$draft().dates.dates.length >= SCHEDULE_DATES_MAX);

  protected readonly $addDateOpen = signal(false);

  protected toggleAddDate(): void {

    if (this.$readonly() || this.$datesFull()) return;

    this.$addDateOpen.update(open => !open);
  }

  protected closeAddDate(): void {

    this.$addDateOpen.set(false);
  }

  /** Range-checked against the line before it lands: a date outside the line would never materialise. */
  protected onAddDate(date: string): void {

    this.$addDateOpen.set(false);
    if (this.$readonly() || !this.isWithinLine(date)) return;

    this.$draft.update(draft => ({ ...draft, dates: { ...draft.dates, dates: addScheduleDate(draft.dates.dates, date) } }));
  }

  protected onRemoveDate(date: string): void {

    if (this.$readonly()) return;

    this.$draft.update(draft => ({ ...draft, dates: { ...draft.dates, dates: removeScheduleDate(draft.dates.dates, date) } }));
  }

  protected onDatesNonWorkingDayPolicy(nonWorkingDayPolicy: ScheduleNonWorkingDayPolicy): void {

    if (this.$readonly() || !nonWorkingDayPolicy) return;

    this.$draft.update(draft => ({ ...draft, dates: { ...draft.dates, nonWorkingDayPolicy } }));
  }

  // ── Calendar: repeat ────────────────────────────────────────────────

  /**
   * The one place the Repeat row's dates sentinel becomes a kind.
   *
   * 'Specific dates' rides in the Repeat row because that is where a scheduler looks for how often,
   * but it is a kind switch: it leaves the calendar branch untouched so a user who picks it, changes
   * their mind and picks Weekly again finds their pattern exactly as they left it.
   */
  protected onRepeatChange(repeat: ScheduleRepeat | string): void {

    if (this.$readonly() || !repeat) return;

    if (repeat === SPECIFIC_DATES_REPEAT_ID) {
      this.$draft.update(draft => ({ ...draft, kind: 'dates' }));
      return;
    }

    if (this.$kind() === 'dates') this.$draft.update(draft => ({ ...draft, kind: 'calendar' }));

    this.patchCalendar({ repeat: repeat as ScheduleRepeat });
    // Yearly's months chips are a required "which month", not the optional seasonal filter, so the
    // section is always open there whatever the user did on the previous repeat.
    if (repeat === 'yearly') this.$seasonalOpen.set(true);
  }

  // ── Calendar: daily ─────────────────────────────────────────────────

  protected onDailyPreset(dailyPreset: ScheduleDailyPreset): void {

    this.patchCalendar({ dailyPreset });
  }

  protected onDailyInterval(dailyInterval: number): void {

    this.patchCalendar({ dailyInterval });
  }

  // ── Calendar: weekly / fortnightly ──────────────────────────────────

  protected onWeeklyInterval(weeklyInterval: number): void {

    this.patchCalendar({ weeklyInterval });
  }

  protected isWeekdaySelected(weekday: ScheduleWeekday): boolean {

    return (this.$calendar().daysOfWeek ?? []).includes(weekday);
  }

  protected onWeekdayToggle(weekday: ScheduleWeekday): void {

    if (this.$readonly()) return;

    const current = this.$calendar().daysOfWeek ?? [];
    const daysOfWeek = current.includes(weekday) ? current.filter(day => day !== weekday) : [...current, weekday].sort((a, b) => a - b);

    this.patchCalendar({ daysOfWeek });
  }

  /**
   * Every card names ISO week numbers, because that is how customers quote a fortnight: A/B name the
   * Monday they start on and its week, the ISO pair list their next three weeks from the anchor week.
   */
  protected weekSelectorNote(selector: { value: 'A' | 'B' | 'isoOdd' | 'isoEven' }): string {

    const weekA = startOfIsoWeek(this.$anchor());

    if (selector.value === 'A') return `starts ${this.$weekAStart()} · wk ${isoWeekNumber(weekA)}`;
    if (selector.value === 'B') return `starts ${this.$weekBStart()} · wk ${isoWeekNumber(addCalendarDays(weekA, 7))}`;

    return `wk ${isoParityWeeks(weekA, selector.value, 3).join(', ')} …`;
  }

  protected onWeekSelector(weekSelector: 'A' | 'B' | 'isoOdd' | 'isoEven'): void {

    this.patchCalendar({ weekSelector });
  }

  // ── Calendar: monthly family ────────────────────────────────────────

  protected onMonthlyOn(monthlyOn: ScheduleMonthlyOn): void {

    this.patchCalendar({ monthlyOn });
  }

  protected onDaysOfMonth(daysOfMonth: DayOfMonthValue[]): void {

    if (this.$readonly()) return;

    this.patchCalendar({ daysOfMonth });
  }

  protected isNthSelected(nth: ScheduleNth): boolean {

    return (this.$calendar().nths ?? []).includes(nth);
  }

  protected onNthToggle(nth: ScheduleNth): void {

    if (this.$readonly()) return;

    const current = this.$calendar().nths ?? [];
    const nths = current.includes(nth) ? current.filter(value => value !== nth) : [...current, nth];

    this.patchCalendar({ nths });
  }

  /** `activeCommandId` matches `Command._id`, which the weekday options spell as the ISO number. */
  protected readonly $weekdayId = computed(() => String(this.$calendar().weekday ?? 1));

  protected onWeekday(weekday: ScheduleWeekday): void {

    if (!weekday) return;

    this.patchCalendar({ weekday });
  }

  protected onMonthInterval(monthInterval: number): void {

    this.patchCalendar({ monthInterval });
  }

  // ── Calendar: months ────────────────────────────────────────────────

  protected isMonthSelected(month: number): boolean {

    return (this.$calendar().months ?? []).includes(month);
  }

  protected onMonthToggle(month: number): void {

    if (this.$readonly()) return;

    const current = this.$calendar().months ?? [];
    const months = current.includes(month) ? current.filter(value => value !== month) : [...current, month].sort((a, b) => a - b);

    this.patchCalendar({ months });
  }

  protected onSeasonalToggle(): void {

    if (this.$readonly()) return;

    const open = !this.$seasonalOpen();
    this.$seasonalOpen.set(open);
    // Closing the section drops the filter — leaving months set while they are off-screen would apply
    // a seasonal restriction the user can no longer see.
    if (!open) this.patchCalendar({ months: [] });
  }

  // ── Calendar: ends ──────────────────────────────────────────────────

  protected readonly $endsType = computed(() => this.$calendar().ends.type);

  protected readonly $endsDate = computed(() => {

    const ends = this.$calendar().ends;

    return ends.type === 'onDate' ? ends.date : '';
  });

  protected readonly $endsCount = computed(() => {

    const ends = this.$calendar().ends;

    return ends.type === 'afterCount' ? ends.count : 12;
  });

  protected onEndsType(type: string): void {

    if (this.$readonly() || !type) return;

    if (type === 'onDate') {
      this.patchCalendar({ ends: { type: 'onDate', date: this.$endsDate() } });
      return;
    }

    if (type === 'afterCount') {
      this.patchCalendar({ ends: { type: 'afterCount', count: this.$endsCount() } });
      return;
    }

    this.patchCalendar({ ends: { type: 'never' } });
  }

  protected onEndsDate(date: string): void {

    this.patchCalendar({ ends: { type: 'onDate', date } });
  }

  protected onEndsCount(count: number): void {

    this.patchCalendar({ ends: { type: 'afterCount', count } });
  }

  protected readonly $endsDateLabel = computed(() => {

    const date = this.$endsDate();

    return date ? formatScheduleDate(date) : 'Pick a date';
  });

  // ── Calendar: non-working day ───────────────────────────────────────

  protected onNonWorkingDayPolicy(nonWorkingDayPolicy: ScheduleNonWorkingDayPolicy): void {

    if (!nonWorkingDayPolicy) return;

    this.patchCalendar({ nonWorkingDayPolicy });
  }

  // ── Footer ──────────────────────────────────────────────────────────

  protected cancel(): void {

    this.dialogRef.dismiss();
  }

  /**
   * Hands back the schedule and the two task-level dates as one answer.
   *
   * Gated on the shared validator, not on a local rule list: what this dialog accepts is exactly what
   * the API guard accepts, so a schedule that saves here cannot be rejected on the way to storage.
   */
  protected save(): void {

    if (!this.$canSave()) return;

    this.dialogRef.close(toResult(this.$draft()));
  }

  // ── Internals ───────────────────────────────────────────────────────

  /** Whether a picked date falls inside the line's own window. An open-ended line has no upper bound. */
  private isWithinLine(date: string): boolean {

    if (!date || date < this.$effectiveFrom()) return false;

    const effectiveTo = this.$effectiveTo();

    return !effectiveTo || date <= effectiveTo;
  }

  private patchCalendar(patch: Partial<ScheduleCalendarRule>): void {

    if (this.$readonly()) return;

    this.$draft.update(draft => ({ ...draft, calendar: { ...draft.calendar, ...patch } }));
  }

  private patchLeeway(side: 'earlyLeeway' | 'lateLeeway', leeway: ScheduleLeeway): void {

    this.patchAfterService(side === 'earlyLeeway' ? { earlyLeeway: leeway } : { lateLeeway: leeway });
  }

  private patchAfterService(patch: Partial<ScheduleEditorDraft['afterService']>): void {

    if (this.$readonly()) return;

    this.$draft.update(draft => ({ ...draft, afterService: { ...draft.afterService, ...patch } }));
  }

  /**
   * The context the preview is evaluated against.
   *
   * Template mode drops the holidays rather than trusting its host to have passed none. A holiday set
   * is a resolved region's closures for named years, and a definition is not applied to a region or a
   * year yet — so honouring one here would shift the example dates by a rule that will not be the rule
   * when the plan is actually created. The working week stays: it is a cadence, not a calendar.
   */
  private context(): ScheduleContext {

    return {
      effectiveFrom: this.$anchor(),
      effectiveTo: this.$effectiveTo(),
      timezone: this.$timezone(),
      workWeek: this.$workWeek(),
      holidays: this.$isTemplate() ? [] : this.$holidays(),
      today: this.$today(),
    };
  }

  private addYears(date: string, years: number): string {

    const [year, rest] = [Number(date.slice(0, 4)), date.slice(4)];

    return `${year + years}${rest}`;
  }
}

/**
 * The options a caller allows, matched on the value each option carries rather than its id.
 *
 * `data` is the value that reaches the model, so filtering on it is what makes the allow-list a
 * statement about schedules rather than about this component's labelling. An undefined allow-list
 * means "no opinion" and every option survives — the live surfaces pass nothing.
 */
function filterOptions(options: readonly Command[], allowed: readonly string[] | undefined): readonly Command[] {

  if (!allowed) return options;

  return options.filter(option => allowed.includes(String(option.data)));
}
